import cx from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { ReactComponent as MinusIcon } from '../../../../../../assets/images/minus.svg';
import { ReactComponent as PlusIcon } from '../../../../../../assets/images/plus.svg';
import Modal from 'react-modal';
import { updateAutoAssignSettingsService } from '../../../../../../services/cna/assignments/cna';
import './styles.scss';
import { isResponseOk } from '../../../../../../utility/isResponseOk';

const SHIFTS = [
  { label: 'Shift 7-3', enabledField: 'shift_7_3_auto_assign_enabled', amountField: 'shift_7_3_cna_amount' },
  { label: 'Shift 3-11', enabledField: 'shift_3_11_auto_assign_enabled', amountField: 'shift_3_11_cna_amount' },
  { label: 'Shift 11-7', enabledField: 'shift_11_7_auto_assign_enabled', amountField: 'shift_11_7_cna_amount' },
];

const ChangeFutureAssignmentsModal = ({ onCancel, onConfirm }) => (
  <Modal
    isOpen
    className="modal-alert auto-assign-forward-confirm-modal"
    overlayClassName={'modal-overlay'}
    onRequestClose={onCancel}
    contentLabel="Change future assignments"
    ariaHideApp={false}
  >
    <h2 className="modal-header">
      <img src="/images/alert-icon.svg" alt="" />
      <button className="btn btn-icon" onClick={onCancel}>
        <i className="icon-icon-x" />
      </button>
    </h2>
    <div>
      <h3>This will change all future assignments</h3>
    </div>
    <div className="modal-footer">
      <button className="btn btn-outline" onClick={onCancel}>
        Cancel
      </button>
      <button className="btn" onClick={onConfirm}>
        Yes
      </button>
    </div>
  </Modal>
);

const AutoAssignSettingsModal = ({ onClose, assignment, onRefetchAssignment }) => {
  const savedSettings = assignment?.assignment_unit_settings;

  const [formData, setFormData] = useState(() =>
    SHIFTS.reduce(
      (data, { enabledField, amountField }) => ({
        ...data,
        [enabledField]: Boolean(savedSettings?.[enabledField]),
        [amountField]: savedSettings?.[amountField] || 1,
      }),
      {},
    ),
  );
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  // Turning a shift off or changing its CNA amount wipes the auto-assigned CNAs of the
  // shifts that are already generated, so the user has to confirm it first. Enabling a
  // shift that was off only adds assignments, which needs no confirmation.
  const changesFutureAssignments = useMemo(
    () =>
      SHIFTS.some(({ enabledField, amountField }) => {
        if (!savedSettings?.[enabledField]) return false;

        return !formData[enabledField] || formData[amountField] !== savedSettings?.[amountField];
      }),
    [formData, savedSettings],
  );

  const setShiftEnabled = (enabledField, isEnabled) => setFormData((state) => ({ ...state, [enabledField]: isEnabled }));

  const setShiftAmount = (amountField, amount) => setFormData((state) => ({ ...state, [amountField]: Math.max(amount, 1) }));

  const updateAutoAssignSettings = useCallback(async () => {
    const response = await updateAutoAssignSettingsService(assignment.id, formData);
    if (isResponseOk(response)) {
      // The saved settings are applied to the shifts that are already generated, so pull
      // the assignment back in to show the CNAs the current shift has just been given.
      await onRefetchAssignment();
      onClose();
    }
  }, [assignment.id, formData, onRefetchAssignment, onClose]);

  const handleSave = useCallback(() => {
    if (changesFutureAssignments) {
      setIsOpenConfirmModal(true);
      return;
    }

    updateAutoAssignSettings();
  }, [changesFutureAssignments, updateAutoAssignSettings]);

  return (
    <Modal
      isOpen
      className={cx('modal-default auto-assign-settings-modal')}
      overlayClassName={'modal-overlay'}
      onRequestClose={onClose}
      contentLabel="Auto Assign Forward"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        Auto-Assign Forward
        <button className="btn btn-icon" onClick={onClose}>
          <i className="icon-icon-x" />
        </button>
      </h2>
      <div className="modal-body">
        {SHIFTS.map(({ label, enabledField, amountField }) => (
          <div className="form-wrapper" key={enabledField}>
            <div className={`form-control-checkbox mt-2`}>
              <input type="checkbox" checked={formData[enabledField]} onChange={(e) => setShiftEnabled(enabledField, e.target.checked)} />
              <span className="shift-label">{label}</span>
            </div>
            {formData[enabledField] && (
              <div className="cna-amount-wrapper">
                <p className="cna-amount-label">CNA amount</p>
                <div className="cna-amount-counter-wrapper">
                  <button className="cna-amount-counter-btn" onClick={() => setShiftAmount(amountField, formData[amountField] - 1)}>
                    <MinusIcon />
                  </button>
                  <div className="cna-amount-counter-number">{formData[amountField]}</div>
                  <button className="cna-amount-counter-btn" onClick={() => setShiftAmount(amountField, formData[amountField] + 1)}>
                    <PlusIcon />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button className="btn" onClick={handleSave}>
          Save
        </button>
      </div>
      {isOpenConfirmModal && (
        <ChangeFutureAssignmentsModal
          onCancel={() => setIsOpenConfirmModal(false)}
          onConfirm={() => {
            setIsOpenConfirmModal(false);
            updateAutoAssignSettings();
          }}
        />
      )}
    </Modal>
  );
};

export default AutoAssignSettingsModal;
